import React, { createContext, useContext, useState, useCallback } from "react";

interface LoadingContextType {
  globalLoading: boolean;
  chartLoading: boolean;
  reportLoading: boolean;
  withLoading: <T>(operation: () => Promise<T>) => Promise<T>;
  withChartLoading: <T>(operation: () => Promise<T>) => Promise<T>;
  withReportLoading: <T>(operation: () => Promise<T>) => Promise<T>;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

interface LoadingProviderProps {
  children: React.ReactNode;
}

export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [loadingCount, setLoadingCount] = useState(0);
  const [chartLoadingCount, setChartLoadingCount] = useState(0);
  const [reportLoadingCount, setReportLoadingCount] = useState(0);

  const withLoading = useCallback(async <T,>(operation: () => Promise<T>): Promise<T> => {
    setLoadingCount((count) => count + 1);
    try {
      return await operation();
    } finally {
      setLoadingCount((count) => count - 1);
    }
  }, []);

  const withChartLoading = useCallback(async <T,>(operation: () => Promise<T>): Promise<T> => {
    setChartLoadingCount((count) => count + 1);
    try {
      return await operation();
    } finally {
      setChartLoadingCount((count) => count - 1);
    }
  }, []);

  const withReportLoading = useCallback(async <T,>(operation: () => Promise<T>): Promise<T> => {
    setReportLoadingCount((count) => count + 1);
    try {
      return await operation();
    } finally {
      setReportLoadingCount((count) => count - 1);
    }
  }, []);

  const globalLoading = loadingCount > 0;
  const chartLoading = chartLoadingCount > 0;
  const reportLoading = reportLoadingCount > 0;

  return (
    <LoadingContext.Provider
      value={{ globalLoading, chartLoading, reportLoading, withLoading, withChartLoading, withReportLoading }}
    >
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = (): LoadingContextType => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error("useLoading must be used within a LoadingProvider");
  }
  return context;
};
